import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { api, ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"

const schema = z
  .object({
    password: z.string().min(8),
    password_confirmation: z.string().min(8),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Hasła nie są identyczne",
    path: ["password_confirmation"],
  })

type ResetPasswordForm = z.infer<typeof schema>

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: ResetPasswordForm) => {
    try {
      setError(null)
      await api.post("/api/v1/auth/password/reset", {
        token,
        password: data.password,
      })
      setSuccess(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  if (success) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="page-title">
          {t("auth.passwordChanged")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("auth.passwordChangedDescription")}
        </p>
        <Link
          to="/login"
          className="inline-block text-sm text-primary hover:underline"
        >
          {t("auth.login")}
        </Link>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="page-title">
          {t("auth.invalidToken")}
        </h2>
        <Link
          to="/forgot-password"
          className="inline-block text-sm text-primary hover:underline"
        >
          {t("auth.requestNewLink")}
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h2 className="page-title">
        {t("auth.resetPassword")}
      </h2>

      {error && (
        <div
          className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.newPassword")}
        </label>
        <input
          type="password"
          autoComplete="new-password"
          {...register("password")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${errors.password ? "border-red-500" : "border-input"}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.confirmPassword")}
        </label>
        <input
          type="password"
          autoComplete="new-password"
          {...register("password_confirmation")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${errors.password_confirmation ? "border-red-500" : "border-input"}`}
        />
        {errors.password_confirmation && (
          <p className="text-xs text-red-500">
            {errors.password_confirmation.message}
          </p>
        )}
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t("common.loading") : t("auth.resetPassword")}
      </Button>
    </form>
  )
}
